// ThreadActionCard — the committed-action card inside an action_raised thread
// entry. An action has an owner (a party, plus an optional internal owner), a
// due date, and a derived state. The colour discipline is the whole point:
// Open and Done are BOTH neutral slate — completing a remedial action is the
// supplier doing what they owed, not a celebration, so a green "Done" pill
// would mis-frame it. Only Overdue (past due, not done) carries red. A late
// completion renders the Done pill neutrally AND surfaces a separate amber
// "(N days late)" annotation so the lateness signal isn't absorbed into a
// misleading positive pill. State + lateness are derived on read via the
// shared deriveIssueDisplay so the card can never drift from the summary tile.
//
// Layout: a two-column [description | meta] grid on `sm:` and up; on narrow
// (mobile) widths it stacks to a single column so the description gets full
// width instead of being squeezed to one-word-per-line beside the late-tag +
// done-pill.

import { clsx } from "clsx";
import {
  deriveIssueActionState,
  deriveIssueDisplay,
} from "../deriveIssueDisplay";
import { getBrisbaneToday } from "../../../../../utils/getBrisbaneToday";
import type { DeliverableOwner } from "../../../../../types";

export interface ThreadActionCardAction {
  description: string;
  party: DeliverableOwner;
  /** Resolved internal owner display name, if any. */
  ownerName?: string;
  /** YYYY-MM-DD; optional — an action may be raised with no due date. */
  dueDate?: string;
  /** YYYY-MM-DD completion date; absent until marked done. */
  doneAt?: string;
}

export interface ThreadActionCardProps {
  action: ThreadActionCardAction;
  today?: Date;
  /** Whether the viewer can mark the action done (editor / admin). */
  canWrite: boolean;
  onMarkDone: () => void;
}

const PARTY_LABEL: Record<DeliverableOwner, string> = {
  supplier: "Supplier",
  customer: "Customer",
  joint: "Joint",
};

function toIsoDay(d: Date): string {
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
  )
    .toISOString()
    .slice(0, 10);
}

const STATE_LABEL = {
  open: "Open",
  done: "Done",
  overdue: "Overdue",
} as const;

// Open + Done share the neutral slate pill on purpose; only Overdue is red.
const STATE_CLASS = {
  open: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
  done: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
  overdue: "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300",
} as const;

function formatDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export default function ThreadActionCard({
  action,
  today,
  canWrite,
  onMarkDone,
}: ThreadActionCardProps) {
  // Lateness is anchored to the Brisbane calendar day (matching the backend's
  // getBrisbaneToday + the parity fixtures), not the browser wall-clock, so a
  // non-AEST viewer near midnight can't see a different lateness than the
  // server. Tests inject a fixed `today` Date for determinism.
  const todayIso = today ? toIsoDay(today) : getBrisbaneToday();
  const state = deriveIssueActionState(
    todayIso,
    action.dueDate,
    action.doneAt,
  );
  const { daysLate } = deriveIssueDisplay({
    today: todayIso,
    // raisedAt / statusEnteredAt aren't relevant to lateness; pass the due
    // date as a harmless anchor so the shared derivation runs.
    raisedAt: action.dueDate ?? todayIso,
    statusEnteredAt: action.dueDate ?? todayIso,
    actionDueDate: action.dueDate,
    actionDoneAt: action.doneAt,
  });
  const isLate = state === "done" && daysLate > 0;

  return (
    <div className="mt-2 grid grid-cols-1 items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-body-sm sm:grid-cols-[1fr_auto] sm:items-center sm:gap-3 dark:border-slate-700 dark:bg-slate-800/60">
      <div className="min-w-0">
        <div className="font-medium text-slate-700 dark:text-slate-200">
          {action.description}
        </div>
        <div className="mt-0.5 text-caption text-slate-500 dark:text-slate-400">
          Owner: {PARTY_LABEL[action.party]}
          {action.ownerName ? ` · ${action.ownerName}` : ""}
          {action.dueDate ? ` · Due ${formatDate(action.dueDate)}` : ""}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {isLate && (
          <span
            data-testid="thread-action-late"
            className="text-caption font-semibold text-amber-700 dark:text-amber-400"
          >
            {daysLate} {daysLate === 1 ? "day" : "days"} late
          </span>
        )}
        <span
          data-testid="thread-action-state"
          className={clsx(
            "rounded px-2 py-0.5 text-caption font-semibold uppercase tracking-wide",
            STATE_CLASS[state],
          )}
        >
          {state === "done" && action.doneAt
            ? `${STATE_LABEL.done} ${formatDate(action.doneAt)}`
            : STATE_LABEL[state]}
        </span>
        {canWrite && state !== "done" && (
          <button
            type="button"
            onClick={onMarkDone}
            className="btn-secondary h-7 px-2 text-caption"
          >
            Mark done
          </button>
        )}
      </div>
    </div>
  );
}
